"use client";

import { Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TechnicalFaq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    value: "item-1",
    question: "What is the purity standard of your peptides?",
    answer: "Every batch undergoes rigorous HPLC and MS testing to ensure a minimum purity of 99%. We maintain strict laboratory protocols to guarantee that our research compounds meet the highest industrial standards for consistency and stability."
  }, {
    value: "item-2",
    question: "How should I store my research compounds?",
    answer: "Lyophilized peptides should be stored in a cool, dry environment away from direct light. For long-term stability, we recommend keeping them at -20°C or lower. Once reconstituted, they should be kept refrigerated at 2-8°C and used within the recommended timeframe."
  }, {
    value: "item-3",
    question: "Where can I find the Certificate of Analysis?",
    answer: "Each product features a dedicated section for batch-specific documentation. You can see the full Certificate of Analysis (COA) directly from our COA's page up at the top."
  }, {
    value: "item-4",
    question: "What is your shipping policy for sensitive materials?",
    answer: "Orders are processed within 24 hours and dispatched via expedited courier services."
  }, {
    value: "item-5",
    question: "Are these products for human consumption?",
    answer: "No. All products sold by Iron Habit Peptides are strictly for laboratory research and analytical use only. They are not intended for human or veterinary consumption, diagnostic use, or any other non-research application."
  }, {
    value: "item-6",
    question: "Do you offer bulk research pricing?",
    answer: "Yes, we support institutional and large-scale research projects with tiered pricing. Please contact our support team to discuss your specific requirements and to receive a formal quote for bulk procurement."
  }, {
    value: "item-8",
    question: "What is your return policy?",
    answer: "Due to the sensitive nature of research compounds, we cannot accept returns once the seal has been broken. If you receive a damaged shipment, please contact us within 48 hours of delivery for a replacement."
  }];
  return <section data-section-id="3156" id="technical-faq" className="bg-background py-24 md:py-48">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24">
              <p className="font-mono text-xs tracking-[0.2em] uppercase mb-6 !text-[rgba(251,189,35,1)]">
                Technical Inquiry
              </p>
              <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.0] text-white mb-6">
                Precision <span className="italic font-light !text-[rgba(251,189,35,1)]">Standards.</span>
              </h2>
              <p className="font-default text-lg leading-relaxed text-gray-400 mb-8 max-w-md">
                Technical specifications and laboratory protocols for the serious researcher. If your query remains unresolved, contact our technical team.
              </p>
              <Link to="/SupportCenterPage" className="inline-block">
                
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full border-t border-border">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={faq.value} value={faq.value} className="border-b border-border last:border-b-0">
                  <AccordionTrigger className="group flex w-full items-center justify-between gap-6 py-8 text-left hover:no-underline [&>svg]:hidden">
                    <span className="font-default font-semibold text-xl leading-snug transition-colors duration-300 group-hover:text-primary !text-[rgba(250,250,249,1)] md:text-[1.4rem]">
                      {faq.question}
                    </span>
                    <span className="relative flex size-8 shrink-0 items-center justify-center text-primary">
                      <span className="absolute h-px w-4 bg-current" />
                      <span className="absolute h-4 w-px bg-current transition-transform duration-500 ease-out group-data-[state=open]:rotate-90 group-data-[state=open]:scale-0" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-12">
                    <p className="font-default text-base leading-[1.7] text-gray-400">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}